import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, AdminInquiryListItem } from '../../api/admin.api';
import {InquiryDetailModal} from "./InquiryDetailModal";

export const InquiryManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('전체');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInquiry, setSelectedInquiry] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleRows, setVisibleRows] = useState(10);

  const tabs = ['전체', '대기중', '진행중', '완료'];
  const rowsPerPage = 10;

  // API 호출 - 상태별 필터링
  const statusMap: Record<string, string | undefined> = {
    전체: undefined,
    대기중: 'PENDING',
    진행중: 'IN_PROGRESS',
    완료: 'COMPLETED',
  };

  const { data: inquiries = [], isLoading } = useQuery({
    queryKey: ['adminInquiries', activeTab],
    queryFn: async () => {
      const response = await adminApi.getAdminInquiries(statusMap[activeTab]);
      return response.data;
    },
  });

  // 검색 필터링
  const filteredData = searchQuery.trim()
    ? inquiries.filter(
        (item) =>
          item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.authorNickname.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : inquiries;

  // 페이지네이션
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // 모바일 "더 보기"
  const mobileData = filteredData.slice(0, visibleRows);

  const getStatusBadgeStyles = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-[#FEF3C7] text-[#92400E]';
      case 'IN_PROGRESS':
        return 'bg-[#DBEAFE] text-[#1E40AF]';
      case 'COMPLETED':
        return 'bg-[#D1FAE5] text-[#065F46]';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '대기중';
      case 'IN_PROGRESS':
        return '진행중';
      case 'COMPLETED':
        return '완료';
      default:
        return status;
    }
  };

  const handleViewDetails = (row: AdminInquiryListItem) => {
    setSelectedInquiry(row);
    setIsModalOpen(true);
  };

  const handleLoadMore = () => {
    setVisibleRows((prev) => prev + 10);
  };

  if (isLoading) {
    return <div className="p-6 text-center">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header + Search */}
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h2 className="text-lg lg:text-xl font-bold text-[#030213]">문의 관리</h2>

          {/* Search */}
          <div className="relative w-full lg:w-80">
            <input
              type="text"
              placeholder="제목 또는 작성자로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#030213]"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4 border-b border-[#E5E7EB]">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                activeTab === tab
                  ? 'text-[#030213]'
                  : 'text-[#6B7280] hover:text-[#030213]'
              }`}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#030213]" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#f3f3f5]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                타입
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                내용
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                작성자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                등록일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E5E7EB]">
            {paginatedData.map((row) => (
              <tr key={row.id} className="hover:bg-[#f3f3f5] transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#030213]">
                  {row.inquiryType}
                </td>
                <td className="px-6 py-4 text-sm text-[#030213] max-w-md truncate">
                  {row.content}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                  {row.authorNickname}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyles(row.status)}`}
                  >
                    {getStatusLabel(row.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                  {new Date(row.regDateTime).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleViewDetails(row)}
                    className="text-[#030213] hover:underline font-medium"
                  >
                    상세보기
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E7EB]">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-sm text-[#6B7280]">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {mobileData.map((row) => (
          <div
            key={row.id}
            className="bg-white rounded-lg shadow-sm p-4"
            onClick={() => handleViewDetails(row)}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs font-medium text-[#6B7280]">{row.inquiryType}</span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyles(row.status)}`}
              >
                {getStatusLabel(row.status)}
              </span>
            </div>
            <p className="text-sm font-medium text-[#030213] mb-2 line-clamp-2">
              {row.content}
            </p>
            <div className="flex justify-between items-center text-xs text-[#6B7280]">
              <span>{row.authorNickname}</span>
              <span>{new Date(row.regDateTime).toLocaleDateString('ko-KR')}</span>
            </div>
          </div>
        ))}

        {/* Load More Button */}
        {visibleRows < filteredData.length && (
          <button
            onClick={handleLoadMore}
            className="w-full py-3 text-sm font-medium text-[#030213] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] transition-colors"
          >
            더 보기
          </button>
        )}
      </div>

      {/* Detail Modal */}
      {isModalOpen && selectedInquiry && (
        <InquiryDetailModal
          inquiry={selectedInquiry}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};
